import { Navigate, createBrowserRouter, RouterProvider } from "react-router-dom";

import { AccessDenied, FLASHPAY_ROLES, RequireAuth, RequireRole } from "@/core/auth";
import type { FlashpayRole } from "@/core/auth";
import { APP_ACCESS_ROLES } from "@/core/auth/roles";
import { ErrorFallback } from "@/core/components/shared/app-error-boundary-fallback";
import { NotFound } from "@/routes/not-found";
import { AppLayout } from "@/routes/layouts/app.layout";
import { GuestLayout } from "@/routes/layouts/guest.layout";
import { RootLayout } from "@/routes/layouts/root.layout";
import { LoginPage } from "@/routes/login";
import { ForgotPasswordPage } from "@/routes/forgot-password";
// Phase 14 — gate OTP post-login (avant redirection /app).
import { VerifyOtpPage } from "@/routes/verify-otp";
import { DashboardPage } from "@/routes/dashboard/dashboard.page";
import { ProfilePage } from "@/routes/settings/profile.page";
// Phase 22 — Delta v4.1 : changement de mot de passe en session (`Users/me/password`).
import { SecurityPage } from "@/routes/settings/security.page";
import { AgentsPage } from "@/routes/kyc/agents.page";
import { KycCaseDetailPage } from "@/routes/kyc/kyc-case-detail.page";
import { KycCasesPage } from "@/routes/kyc/kyc-cases.page";
import { OnboardingPage } from "@/routes/kyc/onboarding.page";
import { PartiesPage } from "@/routes/kyc/parties.page";
import { PartyDetailPage } from "@/routes/kyc/party-detail.page";
import { TransactionDetailPage } from "@/routes/wallet/transaction-detail.page";
import { TransactionsPage } from "@/routes/wallet/transactions.page";
import { WalletDetailPage } from "@/routes/wallet/wallet-detail.page";
import { WalletsPage } from "@/routes/wallet/wallets.page";
import { EmoneyProvisioningPage } from "@/routes/wallet/emoney.page";
import { ScanTransferPage } from "@/routes/wallet/scan-transfer.page";
import { SavingsVaultsPage } from "@/routes/wallet/savings-vaults.page";
import { SavingsVaultDetailPage } from "@/routes/wallet/savings-vault-detail.page";
import { DisputesPage } from "@/routes/wallet/disputes.page";
import { DisputeDetailPage } from "@/routes/wallet/dispute-detail.page";
import { MerchantsPage } from "@/routes/merchant/merchants.page";
import { MerchantDetailPage } from "@/routes/merchant/merchant-detail.page";
import { MerchantMePage } from "@/routes/merchant/merchant-me.page";
import { QrCodesPage } from "@/routes/merchant/qr-codes.page";
import { FeeRulesPage } from "@/routes/fees/fee-rules.page";
import { PromotionsPage } from "@/routes/fees/promotions.page";
import { SubscriptionPlansPage } from "@/routes/fees/subscription-plans.page";
import { CompanySubscriptionsPage } from "@/routes/fees/company-subscriptions.page";
import { NotificationTemplatesPage } from "@/routes/notifications/notification-templates.page";
import { NotificationLogsPage } from "@/routes/notifications/notification-logs.page";
import { NotificationPreferencesPage } from "@/routes/notifications/notification-preferences.page";
import { NotificationCenterPage } from "@/routes/notifications/notification-center.page";
import { PayrollRunsPage } from "@/routes/payroll/payroll-runs.page";
// Phase 22 — Delta v4.1 : paie de masse (payroll-service §6.11)
import { PayrollBatchesPage } from "@/routes/payroll/payroll-batches.page";
import { PayrollBatchDetailPage } from "@/routes/payroll/payroll-batch-detail.page";
import { FundraisingCampaignsPage } from "@/routes/fundraising/fundraising-campaigns.page";
// Phase 6 — Comptabilité (ledger)
import { JournalEntriesPage } from "@/routes/ledger/journal-entries.page";
import { JournalEntryDetailPage } from "@/routes/ledger/journal-entry-detail.page";
import { ChartOfAccountsPage } from "@/routes/ledger/chart-of-accounts.page";
import { PostingTemplatesPage } from "@/routes/ledger/posting-templates.page";
import { PostingTemplateCreatePage } from "@/routes/ledger/posting-template-create.page";
import { ProductCodesPage } from "@/routes/ledger/product-codes.page";
import { ProvidersPage } from "@/routes/ledger/providers.page";
import { ProductProviderMappingsPage } from "@/routes/ledger/product-provider-mappings.page";
import { FxRatesPage } from "@/routes/ledger/fx-rates.page";
// Phase 8 — Administration & maker-checker (admin)
import { ApprovalRequestsPage } from "@/routes/admin/approvals.page";
import { ApprovalRequestDetailPage } from "@/routes/admin/approval-request-detail.page";
import { AuditLogsPage } from "@/routes/admin/audit-logs.page";
import { SystemConfigsPage } from "@/routes/admin/system-configs.page";
import { ApiClientsPage } from "@/routes/admin/api-clients.page";
// Phase 14 — Delta v2.3 : NotificationProviders CRUD
import { NotificationProvidersPage } from "@/routes/notifications/notification-providers.page";
// Phase 15 — Delta v2.4 : QR agent statique
import { AgentMePage } from "@/routes/agents/agent-me.page";
// Phase 16 — Delta v2.5 : Supervision OutboxEvents (6 services)
import { OutboxEventsPage } from "@/routes/admin/outbox-events.page";

/** UX routeur : remplace la page blanche « Unexpected Application Error ». */
function RouteErrorElement() {
  return <ErrorFallback onRefresh={() => window.location.reload()} />;
}

/**
 * Garde de page par rôle (matrice §6.x du guide) : la page n'est rendue que
 * si l'utilisateur a au moins un des rôles exigés, sinon « accès refusé ».
 */
const withRoles = (
  element: React.ReactNode,
  roles: readonly (FlashpayRole | string)[]
) => (
  <RequireRole roles={roles} fallback={<AccessDenied />}>
    {element}
  </RequireRole>
);

/** Rôles back-office KYC : consultation des identités et dossiers (§6.2). */
const KYC_READ_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.AGENTS,
] as const;

/** Rôles d'administration des agents (§6.2). */
const KYC_ADMIN_ROLES = [FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN] as const;

/** Rôles back-office marchands (§6.6). */
const MERCHANT_ADMIN_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
] as const;

/** Rôles back-office tarification (§6.5). */
const FEE_ADMIN_ROLES = [FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN] as const;

/** Rôles gestion des modèles / logs de notifications (§6.7). */
const NOTIFICATION_ADMIN_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
] as const;

/** Rôles ayant accès à la consultation des logs de notifications (§6.7). */
const NOTIFICATION_LOGS_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

/** Phase 9 — Rôles back-office paie & cagnottes (§6.3, opérés pour le compte du client). */
const PAYROLL_FUNDRAISING_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
] as const;

/**
 * Paie de masse et gestion des lots (`/app/payroll`, `/app/payroll/runs`).
 * Réservées au marchand titulaire (`flashpay-merchant`). Superadmin, admin
 * et compte technique n'y ont pas accès. Aligné sur `PAYROLL_BATCH_ROLES`
 * de la nav. Les cagnottes restent sur `PAYROLL_FUNDRAISING_ROLES`.
 */
const PAYROLL_BATCH_ROLES = [FLASHPAY_ROLES.MERCHANT] as const;

/**
 * Phase 6 — Comptabilité (§6.4) : consultation du journal et des référentiels
 * (lectures « authentifié » dans le guide, resserrées au back-office comptable,
 * compte technique inclus pour le journal qu'il alimente via `post`).
 */
const LEDGER_READ_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

/** Phase 6 — Rôles d'administration des référentiels comptables (§6.4). */
const LEDGER_ADMIN_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
] as const;

/**
 * Phase 8 — File d'approbations maker-checker (§6.8) : add/all exposés au
 * compte technique (il dépose des demandes), approve/reject restreints au
 * back-office à l'intérieur des pages.
 */
const ADMIN_APPROVALS_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

/** Phase 8 — Journal d'audit et clients API gateway (§6.8 / §6.1). */
const ADMIN_CONSOLE_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
] as const;

/**
 * Configuration système (§6.8) : même liste que la nav — tout rôle d'accès
 * applicatif sauf le marchand. L'édition reste masquée aux superadmin/admin
 * à l'intérieur de la page.
 */
const SYSTEM_CONFIGS_ROLES = APP_ACCESS_ROLES.filter(
  (role) => role !== FLASHPAY_ROLES.MERCHANT
);

/** Phase 18 — Coffres d'épargne (savings-service, guide v3.1) : agents de terrain (leurs propres coffres) + back-office. */
const SAVINGS_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.AGENTS,
] as const;

/** Phase 18 — Contestations de transfert (file de travail back-office). */
const DISPUTES_ROLES = [FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN] as const;

/** Phase 11 — dashboard live + jauges (guide §7). */
const DASHBOARD_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

/**
 * Portefeuilles et paiement QR : exploitation back-office
 * (même trio que le dashboard). Aligné sur `WALLET_BO_ROLES` de la nav.
 */
const WALLET_BO_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

/**
 * Historique des transactions : back-office et marchand titulaire.
 * Aligné sur `WALLET_TX_ROLES` de la nav.
 */
const WALLET_TX_ROLES = [
  ...WALLET_BO_ROLES,
  FLASHPAY_ROLES.MERCHANT,
] as const;

/**
 * Détail d'un marchand : back-office et marchand titulaire (son propre
 * compte, sinon 404). La liste `/app/merchants` reste `MERCHANT_ADMIN_ROLES`.
 */
const MERCHANT_DETAIL_ROLES = [
  ...MERCHANT_ADMIN_ROLES,
  FLASHPAY_ROLES.MERCHANT,
] as const;

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RouteErrorElement />,
    children: [
      // La racine renvoie vers la zone applicative ; RequireAuth redirige
      // vers le SSO si la session n'est pas (ou plus) établie.
      { index: true, element: <Navigate to="/app" replace /> },
      {
        path: "app",
        element: (
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        ),
        children: [
          { index: true, element: withRoles(<DashboardPage />, DASHBOARD_ROLES) },
          {
            path: "settings/profile",
            element: <ProfilePage />,
          },
          // Phase 22 — Delta v4.1 : « Mon compte / Sécurité » (changement de
          // mot de passe en session). Visible pour tous les rôles authentifiés
          // ayant accès à l'app (pas de garde par rôle supplémentaire).
          {
            path: "account/security",
            element: <SecurityPage />,
          },
          {
            path: "kyc/parties",
            element: withRoles(<PartiesPage />, KYC_READ_ROLES),
          },
          {
            path: "kyc/parties/:partyId",
            element: withRoles(<PartyDetailPage />, KYC_READ_ROLES),
          },
          {
            path: "kyc/cases",
            element: withRoles(<KycCasesPage />, KYC_READ_ROLES),
          },
          // Détail d'un dossier : tout utilisateur authentifié (§6.2).
          { path: "kyc/cases/:kycCaseId", element: <KycCaseDetailPage /> },
          {
            path: "kyc/agents",
            element: withRoles(<AgentsPage />, KYC_ADMIN_ROLES),
          },
          { path: "kyc/onboarding", element: withRoles(<OnboardingPage />, KYC_READ_ROLES) },
          {
            path: "wallet",
            element: withRoles(<WalletsPage />, WALLET_BO_ROLES),
          },
          {
            path: "wallet/emoney",
            element: withRoles(<EmoneyProvisioningPage />, FEE_ADMIN_ROLES),
          },
          {
            path: "wallet/scan-transfer",
            element: withRoles(<ScanTransferPage />, WALLET_BO_ROLES),
          },
          {
            path: "wallet/transactions",
            element: withRoles(<TransactionsPage />, WALLET_TX_ROLES),
          },
          {
            path: "wallet/transactions/:transactionId",
            element: withRoles(<TransactionDetailPage />, WALLET_TX_ROLES),
          },
          // Phase 18 — Coffres d'épargne (savings-service) & contestations (guide v3.1).
          {
            path: "wallet/savings-vaults",
            element: withRoles(<SavingsVaultsPage />, SAVINGS_ROLES),
          },
          {
            path: "wallet/savings-vaults/:id",
            element: withRoles(<SavingsVaultDetailPage />, SAVINGS_ROLES),
          },
          {
            path: "wallet/disputes",
            element: withRoles(<DisputesPage />, DISPUTES_ROLES),
          },
          {
            path: "wallet/disputes/:id",
            element: withRoles(<DisputeDetailPage />, DISPUTES_ROLES),
          },
          {
            path: "wallet/:walletId",
            element: withRoles(<WalletDetailPage />, WALLET_BO_ROLES),
          },
          // Phase 4 — Marchands & QR (§6.6).
          {
            path: "merchants",
            element: withRoles(<MerchantsPage />, MERCHANT_ADMIN_ROLES),
          },
          // Avant `:merchantId` : « me » ne doit pas être pris pour un id.
          // Réservé au titulaire — la liste `/app/merchants` reste back-office.
          {
            path: "merchants/me",
            element: withRoles(<MerchantMePage />, [FLASHPAY_ROLES.MERCHANT]),
          },
          {
            path: "merchants/:merchantId",
            element: withRoles(<MerchantDetailPage />, MERCHANT_DETAIL_ROLES),
          },
          {
            path: "merchants/:merchantId/qr-codes",
            element: withRoles(<QrCodesPage />, MERCHANT_DETAIL_ROLES),
          },
          // Phase 5 — Frais & Tarification (§6.5).
          {
            path: "fees/rules",
            element: withRoles(<FeeRulesPage />, FEE_ADMIN_ROLES),
          },
          {
            path: "fees/promotions",
            element: withRoles(<PromotionsPage />, FEE_ADMIN_ROLES),
          },
          {
            path: "fees/plans",
            element: withRoles(<SubscriptionPlansPage />, FEE_ADMIN_ROLES),
          },
          {
            path: "fees/subscriptions",
            element: withRoles(<CompanySubscriptionsPage />, FEE_ADMIN_ROLES),
          },
          // Phase 6 — Comptabilité (§6.4).
          {
            path: "ledger/journal",
            element: withRoles(<JournalEntriesPage />, LEDGER_READ_ROLES),
          },
          {
            path: "ledger/journal/:journalEntryId",
            element: withRoles(<JournalEntryDetailPage />, LEDGER_READ_ROLES),
          },
          {
            path: "ledger/chart-of-accounts",
            element: withRoles(<ChartOfAccountsPage />, LEDGER_ADMIN_ROLES),
          },
          {
            path: "ledger/posting-templates",
            element: withRoles(<PostingTemplatesPage />, LEDGER_ADMIN_ROLES),
          },
          {
            path: "ledger/posting-templates/new",
            element: withRoles(
              <PostingTemplateCreatePage />,
              LEDGER_ADMIN_ROLES
            ),
          },
          {
            path: "ledger/product-codes",
            element: withRoles(<ProductCodesPage />, LEDGER_ADMIN_ROLES),
          },
          {
            path: "ledger/providers",
            element: withRoles(<ProvidersPage />, LEDGER_ADMIN_ROLES),
          },
          {
            path: "ledger/product-provider-mappings",
            element: withRoles(
              <ProductProviderMappingsPage />,
              LEDGER_ADMIN_ROLES
            ),
          },
          {
            path: "ledger/fx-rates",
            element: withRoles(<FxRatesPage />, LEDGER_ADMIN_ROLES),
          },
          // Phase 7 — Notifications (§6.7).
          // Centre de notifications : boîte de réception locale (FCM) de
          // tout utilisateur authentifié — pas de garde de rôle, pas
          // d'entrée de nav (accès via la cloche du header).
          { path: "notifications/center", element: <NotificationCenterPage /> },
          {
            path: "notifications/templates",
            element: withRoles(
              <NotificationTemplatesPage />,
              NOTIFICATION_ADMIN_ROLES
            ),
          },
          {
            path: "notifications/logs",
            element: withRoles(<NotificationLogsPage />, NOTIFICATION_LOGS_ROLES),
          },
          {
            path: "notifications/preferences",
            element: withRoles(
              <NotificationPreferencesPage />,
              NOTIFICATION_ADMIN_ROLES
            ),
          },
          // Phase 14 — Delta v2.3 : NotificationProviders CRUD.
          {
            path: "notifications/providers",
            element: withRoles(
              <NotificationProvidersPage />,
              NOTIFICATION_ADMIN_ROLES
            ),
          },
          // Phase 9 — Paie & Cagnottes (types de transaction du wallet-service, §6.3).
          {
            path: "payroll/runs",
            element: withRoles(<PayrollRunsPage />, PAYROLL_BATCH_ROLES),
          },
          {
            path: "fundraising/campaigns",
            element: withRoles(
              <FundraisingCampaignsPage />,
              PAYROLL_FUNDRAISING_ROLES
            ),
          },
          // Phase 22 — Paie de masse (payroll-service v4.1 §6.11).
          {
            path: "payroll",
            element: withRoles(<PayrollBatchesPage />, PAYROLL_BATCH_ROLES),
          },
          {
            path: "payroll/:batchId",
            element: withRoles(<PayrollBatchDetailPage />, PAYROLL_BATCH_ROLES),
          },
          // Phase 8 — Administration & maker-checker (§6.8 / §6.1 gateway).
          {
            path: "admin/approvals",
            element: withRoles(<ApprovalRequestsPage />, ADMIN_APPROVALS_ROLES),
          },
          {
            path: "admin/approvals/:approvalRequestId",
            element: withRoles(
              <ApprovalRequestDetailPage />,
              ADMIN_APPROVALS_ROLES
            ),
          },
          {
            path: "admin/audit-logs",
            element: withRoles(<AuditLogsPage />, ADMIN_CONSOLE_ROLES),
          },
          {
            path: "admin/system-configs",
            element: withRoles(<SystemConfigsPage />, SYSTEM_CONFIGS_ROLES),
          },
          {
            path: "admin/api-clients",
            element: withRoles(<ApiClientsPage />, ADMIN_CONSOLE_ROLES),
          },
          // Phase 16 — Delta v2.5 : supervision OutboxEvents (6 services).
          {
            path: "admin/outbox",
            element: withRoles(<OutboxEventsPage />, ADMIN_CONSOLE_ROLES),
          },
          // Phase 15 — Delta v2.4 : page agent (QR statique pour dépôt espèces).
          {
            path: "agents/me",
            element: withRoles(<AgentMePage />, [FLASHPAY_ROLES.AGENTS] as const),
          },
        ],
      },
      {
        path: "login",
        element: <GuestLayout />,
        children: [{ index: true, element: <LoginPage /> }],
      },
      {
        path: "forgot-password",
        element: <GuestLayout />,
        children: [{ index: true, element: <ForgotPasswordPage /> }],
      },
      // Gate OTP post-login : rendu directement (pas de GuestLayout, pas de
      // RequireAuth — la session est vérifiée dans la page elle-même).
      { path: "verify-otp", element: <VerifyOtpPage /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
